import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  createRoutineSchema,
  updateRoutineSchema,
  ROUTINE_DAYPARTS,
  ROUTINE_CATEGORIES,
  type CreateRoutineInput,
} from "@shared/validation/routine.schema";
import { useCreateRoutine, useUpdateRoutine, type RoutineSummary } from "../../lib/api/useRoutines";
import { useToast } from "../../lib/toast/ToastProvider";
import { ApiError } from "../../lib/api/client";
import { INPUT_CLASS } from "../../lib/forms/inputClass";
import { DialogShell } from "../ui/DialogShell";
import { Field } from "../ui/Field";
import { Button } from "../ui/Button";

const DAYPART_LABELS: Record<(typeof ROUTINE_DAYPARTS)[number], string> = {
  morning: "Morning",
  midday: "Midday",
  afternoon: "Afternoon",
  evening: "Evening",
  bedtime: "Bedtime",
};

const CATEGORY_LABELS: Record<(typeof ROUTINE_CATEGORIES)[number], string> = {
  nap: "Nap",
  meal: "Meal",
  activity: "Activity",
  medication: "Medication",
  bedtime: "Bedtime",
  other: "Other",
};

interface RoutineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  childId: string;
  routine?: RoutineSummary;
}

export function RoutineDialog({ open, onOpenChange, childId, routine }: RoutineDialogProps) {
  const isEdit = Boolean(routine);
  const createMutation = useCreateRoutine(childId);
  const updateMutation = useUpdateRoutine(childId, routine?.id ?? "");
  const activeMutation = isEdit ? updateMutation : createMutation;
  const { publish } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<CreateRoutineInput>({
    resolver: zodResolver(isEdit ? updateRoutineSchema : createRoutineSchema),
    defaultValues: {
      label: routine?.label ?? "",
      daypart: routine?.daypart ?? "morning",
      category: routine?.category ?? "activity",
      timeText: routine?.timeText ?? null,
      details: routine?.details ?? null,
      notes: routine?.notes ?? null,
    },
  });

  // Reset only on the closed→open transition (see AllergyDialog: mutation
  // identities change each render, so an unguarded effect would loop and wipe
  // user input).
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      reset({
        label: routine?.label ?? "",
        daypart: routine?.daypart ?? "morning",
        category: routine?.category ?? "activity",
        timeText: routine?.timeText ?? null,
        details: routine?.details ?? null,
        notes: routine?.notes ?? null,
      });
      createMutation.reset();
      updateMutation.reset();
    }
    wasOpen.current = open;
  }, [open, routine, reset, createMutation, updateMutation]);

  const onSubmit = async (data: CreateRoutineInput) => {
    try {
      await activeMutation.mutateAsync(data);
      publish({
        variant: "success",
        title: isEdit ? "Routine updated successfully" : "Routine added successfully",
      });
      onOpenChange(false);
    } catch (err) {
      publish({
        variant: "error",
        title: isEdit ? "Failed to update routine" : "Failed to add routine",
        description: err instanceof ApiError ? err.message : undefined,
      });
    }
  };

  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={isEdit ? "Edit Routine" : "Add Routine"}
      description={
        isEdit
          ? "Update this routine below."
          : "Add a routine so caregivers know the daily rhythm — naps, meals, activities, bedtime."
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
        <Field label="What happens *" htmlFor="routine-label" error={errors.label?.message}>
          <input
            id="routine-label"
            type="text"
            {...register("label")}
            className={INPUT_CLASS}
            placeholder="e.g., Morning nap, Afternoon snack"
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Time of Day *" htmlFor="routine-daypart" error={errors.daypart?.message}>
            <select id="routine-daypart" {...register("daypart")} className={INPUT_CLASS}>
              {ROUTINE_DAYPARTS.map((d) => (
                <option key={d} value={d}>
                  {DAYPART_LABELS[d]}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Category *" htmlFor="routine-category" error={errors.category?.message}>
            <select id="routine-category" {...register("category")} className={INPUT_CLASS}>
              {ROUTINE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Time (optional)" htmlFor="routine-time">
          <input
            id="routine-time"
            type="text"
            {...register("timeText")}
            className={INPUT_CLASS}
            placeholder="e.g., 7:30 AM, After lunch"
          />
        </Field>

        <Field label="Details" htmlFor="routine-details">
          <textarea
            id="routine-details"
            rows={2}
            {...register("details")}
            className={INPUT_CLASS}
            placeholder="How it usually goes, what helps…"
          />
        </Field>

        <Field label="Additional Notes" htmlFor="routine-notes">
          <textarea id="routine-notes" rows={2} {...register("notes")} className={INPUT_CLASS} />
        </Field>

        {activeMutation.error instanceof ApiError && (
          <p className="rounded-xl bg-red-50 p-3 text-sm text-red-700">
            {activeMutation.error.message}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting || activeMutation.isPending}>
            {activeMutation.isPending ? "Saving…" : isEdit ? "Save Changes" : "Add Routine"}
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}
